import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { useAuth } from '@/shared/auth/useAuth';
import { Icon } from '@/shared/ui/icons/Icon';
import { Logo } from '@/shared/ui/Logo';
import { PUBLIC_NAV } from './navigation';

const FOOTER = [
  {
    title: 'Szukaj',
    links: [
      ['Mieszkania na sprzedaż', '/kupno/mieszkania'],
      ['Mieszkania na wynajem', '/wynajem/mieszkania'],
      ['Domy', '/kupno/domy'],
      ['Działki', '/kupno/dzialki'],
      ['Nowe inwestycje', '/nowe-inwestycje'],
    ],
  },
  {
    title: 'Ogłoszeniodawcy',
    links: [
      ['Dodaj ogłoszenie', '/dodaj'],
      ['Cennik', '/cennik'],
      ['Dla agencji', '/dla-agencji'],
      ['Dla deweloperów', '/dla-deweloperow'],
    ],
  },
  {
    title: 'Rynek.',
    links: [
      ['O nas', '/o-nas'],
      ['Raporty cen', '/raporty-cen'],
      ['Dzielnice Warszawy', '/okolica/warszawa'],
      ['Kontakt', '/kontakt'],
    ],
  },
  {
    title: 'Prawne',
    links: [
      ['Regulamin', '/regulamin'],
      ['Prywatność', '/prywatnosc'],
      ['Punkt kontaktowy (DSA)', '/punkt-kontaktowy'],
      ['Cookies', '/cookies'],
    ],
  },
] as const;

/** Część publiczna portalu: nagłówek z nawigacją, jeden ceglasty przycisk „Dodaj ogłoszenie”, stopka (makiety, s. 1–2). */
export function PublicLayout() {
  const { state } = useAuth();
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-line-soft sticky top-0 z-30 border-b bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6">
          <Logo />
          <nav aria-label="Główna" className="hidden items-center gap-1 md:flex">
            {PUBLIC_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  clsx('btn btn-ghost px-3 text-[0.9375rem]', isActive ? 'text-ink' : 'text-ink-2')
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {state.status === 'authenticated' ? (
              <Link to="/panel" className="btn btn-ghost hidden sm:inline-flex">
                <Icon name="konto" size={20} />
                Moje konto
              </Link>
            ) : (
              <Link to="/logowanie" className="btn btn-ghost hidden sm:inline-flex">
                <Icon name="konto" size={20} />
                Zaloguj się
              </Link>
            )}
            <Link to="/dodaj" className="btn btn-brick" aria-label="Dodaj ogłoszenie">
              <Icon name="dodaj-ogloszenie" size={20} />
              <span className="hidden sm:inline">Dodaj ogłoszenie</span>
            </Link>
            <MobileMenu signedIn={state.status === 'authenticated'} />
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="bg-plaster border-line-soft border-t">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-[1.4fr_repeat(4,1fr)]">
          <div>
            <img
              src="/brand/rynek-logo-tagline-poziomo.svg"
              alt="Rynek. Wybierz swoje miejsce"
              className="-ml-[9px] h-12 w-auto sm:-ml-[11px] sm:h-14"
            />
            <p className="text-ink-2 mt-4 max-w-xs text-sm">
              Ogłoszenia nieruchomości w Polsce. Najpierw miejsce, potem mieszkanie.
            </p>
          </div>
          {FOOTER.map((col) => (
            <div key={col.title}>
              <div className="eyebrow mb-3">{col.title}</div>
              <ul className="space-y-2 text-sm">
                {col.links.map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="text-ink-2 hover:text-ink">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="text-ink-4 mx-auto max-w-7xl px-4 pb-8 text-xs sm:px-6">
          © {new Date().getFullYear()} Rynek. Wszystkie prawa zastrzeżone.
        </div>
      </footer>
    </div>
  );
}

/** Telefon: hamburger z menu — nawigacja, konto (logowanie albo panel) i skróty do najczęstszych ekranów panelu. */
function MobileMenu({ signedIn }: { signedIn: boolean }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const close = () => {
    setOpen(false);
  };
  const account: [string, string][] = signedIn
    ? [
        ['Panel', '/panel'],
        ['Moje ogłoszenia', '/panel/ogloszenia'],
        ['Wiadomości', '/panel/wiadomosci'],
        ['Zapisane i alerty', '/panel/zapisane'],
        ['Czego szukam', '/panel/szukam'],
        ['Konto', '/panel/konto'],
      ]
    : [
        ['Zaloguj się', '/logowanie'],
        ['Załóż konto', '/rejestracja'],
      ];
  return (
    <div className="md:hidden">
      <button
        type="button"
        className="btn btn-ghost px-2"
        aria-expanded={open}
        aria-controls="menu-mobilne"
        aria-label={open ? 'Zamknij menu' : 'Otwórz menu'}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 top-16 z-30 bg-black/30" aria-hidden="true" onClick={close} />
          <nav
            id="menu-mobilne"
            aria-label="Menu"
            className="border-line-soft fixed inset-x-0 top-16 z-40 max-h-[calc(100dvh-4rem)] overflow-y-auto border-b bg-white px-4 pb-6 shadow-lg"
          >
            <div className="eyebrow mt-4 mb-1">{signedIn ? 'Moje konto' : 'Konto'}</div>
            <ul>
              {account.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} onClick={close} className="hover:bg-plaster block rounded-lg px-2 py-3 text-base">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="eyebrow mt-4 mb-1">Szukaj</div>
            <ul>
              {PUBLIC_NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} onClick={close} className="hover:bg-plaster block rounded-lg px-2 py-3 text-base">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </>
      )}
    </div>
  );
}
